import type { ScanCandidate } from "@hobbyshopify/backend-client";
import { describeCandidate } from "../core/primitives/candidates";
import { gradePriceText } from "../core/primitives/grades";

/**
 * The products the lookup weighed, for the reviewer to pick between.
 *
 * Shown as cards rather than names alone because the look-alikes differ by a
 * parallel a title can only assert: the photo is what settles a Refractor
 * against a Prism Refractor. The match badge says what confirming does with
 * each — add a copy to a card the shop has, or create one.
 */
export function ScanCandidates({
  candidates,
  selectedProductId,
  picking,
  disabled,
  onPick,
}: {
  candidates: readonly ScanCandidate[];
  selectedProductId: string | null;
  /** The product a pick is in flight for. */
  picking: string | null;
  disabled: boolean;
  onPick: (productId: string) => void;
}) {
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">Identified as</legend>
      {candidates.map((candidate) => {
        const line = describeCandidate(candidate.product);
        const selected = line.productId !== null && line.productId === selectedProductId;
        const inFlight = line.productId !== null && line.productId === picking;
        const image = candidate.images[0];
        return (
          <label
            key={line.productId ?? `rank-${candidate.rank}`}
            className={`flex cursor-pointer items-center gap-3 rounded-row border p-3 transition-colors ${
              selected ? "border-accent bg-accent/10" : "border-line bg-surface hover:border-muted"
            } ${line.productId === null ? "cursor-not-allowed opacity-60" : ""}`}
          >
            <input
              type="radio"
              name="scan-candidate"
              className="accent-accent"
              checked={selected}
              disabled={line.productId === null || inFlight}
              onChange={() => line.productId && onPick(line.productId)}
              aria-label={`Identify as ${line.title}`}
            />
            <div className="h-16 w-12 shrink-0 overflow-hidden rounded border border-line bg-surface-strong">
              {image ? (
                <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[10px] text-faint">no photo</div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-white">{line.title}</div>
              <div className="truncate text-xs text-muted">{line.subtitle}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                {candidate.matchKind === "PRODUCT" ? (
                  <span className="badge badge-accent">Matches stock</span>
                ) : (
                  <span className="badge badge-neutral">New card</span>
                )}
                {gradePriceText("raw", candidate.product) && (
                  <span className="text-faint">raw {gradePriceText("raw", candidate.product)}</span>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right text-xs text-muted">
              {inFlight ? "Switching…" : candidate.confidence !== null ? `${candidate.confidence}% match` : ""}
              {!inFlight && candidate.listingVotes > 0 && (
                <div className="text-[11px] text-faint">
                  {candidate.listingVotes} eBay {candidate.listingVotes === 1 ? "listing" : "listings"}
                </div>
              )}
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}
